import React, { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom'
import { Analytics } from '@vercel/analytics/react'
import posthog from 'posthog-js'
import { useTheme } from './contexts/ThemeContext'
import { useAuth } from './contexts/AuthContext'
import Home from './components/Home'
import M2 from './components/M2'
import M3 from './components/M3'
import M4 from './components/M4'
import M8 from './components/M8'


import Contact from './components/Contact'
import Terms from './components/Terms'
import AuthModal from './components/AuthModal'
import DonateModal from './components/DonateModal'
import AccountSettings from './components/AccountSettings'
import ResetPassword from './pages/ResetPassword'
import VerifyEmail from './pages/VerifyEmail'

// Capture pageviews on route change for PostHog
function PostHogPageView() {
  const location = useLocation()
  useEffect(() => {
    if (typeof window !== 'undefined' && posthog) {
      posthog.capture('$pageview')
    }
  }, [location])
  return null
}

function Navigation({ onSignInClick, onDonateClick }: { onSignInClick: () => void; onDonateClick: () => void }) {
  const { theme, toggleTheme } = useTheme()
  const { user, logout, loading } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [userDropdownOpen, setUserDropdownOpen] = useState(false)
  const userInitial = (
    (user?.name?.charAt(0)) ||
    (user as any)?.first_name?.charAt(0) ||
    user?.email?.charAt(0) ||
    '?'
  ).toUpperCase()

  const ThemeToggle = () => (
    <button
      onClick={toggleTheme}
      className="flex items-center justify-center p-1.5 sm:p-2 rounded bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 transition-colors"
      aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
    >
      {theme === 'light' ? (
        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      ) : (
        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      )}
    </button>
  )

  return (
    <header className="bg-white dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800 sticky top-0 z-40 backdrop-blur-sm bg-white/95 dark:bg-gray-950/95">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-2 sm:space-x-4 md:space-x-8">
            <Link to="/" className="flex items-center space-x-2 sm:space-x-4">
              <h1 className="text-base sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white leading-tight">
                <span className="hidden sm:inline">ineedcarbonbuckets</span>
                <span className="sm:hidden">incb</span>
              </h1>
              {location.pathname === '/' && (
                <div className="hidden md:flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span>Live inventory</span>
                </div>
              )}
            </Link>
            
            {/* Navigation Links */}
            <nav className="hidden md:flex items-center space-x-6">
              <Link 
                to="/" 
                className={`text-sm font-medium transition-colors ${
                  location.pathname === '/' 
                    ? 'text-primary-600 dark:text-primary-400' 
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                All Models
              </Link>
              <Link 
                to="/m2" 
                className={`text-sm font-medium transition-colors ${
                  location.pathname === '/m2' 
                    ? 'text-primary-600 dark:text-primary-400' 
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                M2
              </Link>
              <Link 
                to="/m3" 
                className={`text-sm font-medium transition-colors ${
                  location.pathname === '/m3' 
                    ? 'text-primary-600 dark:text-primary-400' 
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                M3
              </Link>
              <Link 
                to="/m4" 
                className={`text-sm font-medium transition-colors ${
                  location.pathname === '/m4' 
                    ? 'text-primary-600 dark:text-primary-400' 
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                M4
              </Link>
                <Link
                    to="/m8"
                    className={`text-sm font-medium transition-colors ${
                        location.pathname === '/m8'
                            ? 'text-primary-600 dark:text-primary-400'
                            : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                    }`}
                >
                    M8
                </Link>
              <Link 
                to="/contact" 
                className={`text-sm font-medium transition-colors ${
                  location.pathname === '/contact' 
                    ? 'text-primary-600 dark:text-primary-400' 
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Contact
              </Link>
              <Link 
                to="/terms" 
                className={`text-sm font-medium transition-colors ${
                  location.pathname === '/terms' 
                    ? 'text-primary-600 dark:text-primary-400' 
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Terms
              </Link>
            </nav>
          </div>
          
          <div className="flex items-center space-x-1 sm:space-x-2 md:space-x-4 flex-shrink-0">
            {/* Auth Section */}
            {loading ? (
              <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse"></div>
            ) : user ? (
              <div className="relative flex items-center space-x-2 sm:space-x-3">
                <button
                  onClick={onDonateClick}
                  className="hidden sm:inline-flex px-3 py-1.5 text-xs sm:text-sm font-medium text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50 rounded transition-colors"
                >
                  Donate
                </button>
                <button
                  onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                  className="flex items-center space-x-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-white">{userInitial}</span>
                  </div>
                  <span className="hidden sm:inline">{user.name || user.email}</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {userDropdownOpen && (
                  <>
                    {/* Backdrop */}
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setUserDropdownOpen(false)}
                    />
                    {/* Dropdown */}
                    <div className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-20 border border-gray-200 dark:border-gray-700">
                      <Link
                        to="/account"
                        onClick={() => setUserDropdownOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        Account Settings
                      </Link>
                      <div className="border-t border-gray-100 dark:border-gray-700 my-1"></div>
                      <button
                        onClick={() => {
                          logout()
                          navigate('/')
                          setUserDropdownOpen(false)
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        Sign Out
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <button
                onClick={onSignInClick}
                className="px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-950 flex items-center justify-center whitespace-nowrap"
              >
                Sign In
              </button>
            )}
            
            <ThemeToggle />
            
            {/* Mobile Navigation Menu */}
            <div className="md:hidden">
              <div className="relative">
                <select 
                  value={location.pathname} 
                  onChange={(e) => navigate(e.target.value)}
                  className="appearance-none bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-2 pr-6 rounded text-xs border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-20 truncate"
                >
                  <option value="/">All Models</option>
                  <option value="/m2">M2</option>
                  <option value="/m3">M3</option>
                  <option value="/m4">M4</option>
                    <option value="/m4">M8</option>
                    <option value="/contact">Contact</option>
                  <option value="/terms">Terms</option>
                </select>
                <svg className="absolute right-1 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}

function Footer({ onDonateClick }: { onDonateClick: () => void }) {
  return (
    <footer className="bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 mt-16">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8 py-8">
        <div className="text-center space-y-4">
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-6">
            <button
              onClick={onDonateClick}
              className="text-sm text-green-700 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 transition-colors font-medium"
            >
              Support the site
            </button>
            <Link to="/contact" className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
              Contact
            </Link>
            <Link to="/terms" className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
              Terms of Service
            </Link>
          </div>
          <div className="space-y-2">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              We are not associated with any dealership. All trademarks are property of their respective owners.
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500">
              Free to use — sign in to view live inventory
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500">
              © {new Date().getFullYear()} LTDA. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
}

function App() {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false)
  const [isDonateModalOpen, setIsDonateModalOpen] = useState(false)
  const { user } = useAuth()

  const openDonate = () => {
    if (!user) {
      setIsAuthModalOpen(true)
      return
    }
    setIsDonateModalOpen(true)
  }

  return (
    <Router>
      <PostHogPageView />
      <div className="min-h-screen bg-white dark:bg-gray-950 transition-colors overflow-x-hidden">
        <Navigation onSignInClick={() => setIsAuthModalOpen(true)} onDonateClick={openDonate} />
        <Routes>
          <Route path="/" element={<Home onSignInClick={() => setIsAuthModalOpen(true)} />} />
          <Route path="/m2" element={<M2 />} />
          <Route path="/m3" element={<M3 />} />
          <Route path="/m4" element={<M4 />} />
            <Route path="/m8" element={<M8 />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/account" element={<AccountSettings />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
        </Routes>
        <Footer onDonateClick={openDonate} />
        
        <AuthModal 
          isOpen={isAuthModalOpen} 
          onClose={() => setIsAuthModalOpen(false)} 
        />

        <DonateModal
          isOpen={isDonateModalOpen}
          onClose={() => setIsDonateModalOpen(false)}
        />
        
        <Analytics />
      </div>
    </Router>
  )
}

export default App